/**
 * Public Top Bar Component
 * 
 * Header with logo and Login/Signup buttons for fast users.
 */

import React from 'react';
import { Box, Button, Typography, Container, useTheme, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

interface PublicTopBarProps {
  onLoginClick: () => void;
  onSignupClick: () => void;
}

export const PublicTopBar: React.FC<PublicTopBarProps> = ({
  onLoginClick,
  onSignupClick,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box className={classes.topBar}>
      <Container maxWidth="lg" className={classes.container}>
        {/* Logo */}
        <Box className={classes.logoSection}>
          <Box className={classes.logoIcon}>
            <img className={classes.logoImage} src="/dashboard/images/logos/product_logo_white.svg" alt="SAIA logo" />
          </Box>
          {!isMobile && (
            <Typography variant="body2" className={classes.tagline}>
              Your Confidential LinkedIn Agent
            </Typography>
          )}
        </Box>

        {/* Actions */}
        <Box className={classes.actions}>
          <Button
            variant="text"
            onClick={onLoginClick}
            startIcon={<LoginIcon />}
            className={classes.loginButton}
            size={isMobile ? 'small' : 'medium'}
          >
            {t('publicChat.login', 'Login')}
          </Button>
          <Button
            variant="contained"
            onClick={onSignupClick}
            startIcon={!isMobile ? <PersonAddIcon /> : undefined}
            className={classes.signupButton}
            size={isMobile ? 'small' : 'medium'}
          >
            {t('publicChat.signup', 'Sign Up Free')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  topBar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: 64,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  logoIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 2px 8px ${theme.palette.primary.main}33`,
  },
  logoImage: {
    width: 28,
    height: 28,
    objectFit: 'contain',
  },
  tagline: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      gap: theme.spacing(2),
    },
  },
  loginButton: {
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: theme.palette.greys.lightest,
    },
  },
  signupButton: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    color: theme.palette.primary.contrastText,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    padding: theme.spacing(1, 2.5),
    boxShadow: `0 4px 12px ${theme.palette.primary.main}4D`,
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
      boxShadow: `0 6px 16px ${theme.palette.primary.main}66`,
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.75, 1.5),
    },
  },
}));

export default PublicTopBar;

